const trailShader = /* wgsl */ `
struct TrailSimulation {
    width: u32,
    height: u32,
    strainCount: u32,
    padding0: u32,

    decay: vec4f,
    diffusion: vec4f,
    depositStrength: vec4f,
};

@group(0) @binding(0)
var previousTrail: texture_2d<f32>;

@group(0) @binding(1)
var nextTrail: texture_storage_2d<rgba16float, write>;

@group(0) @binding(2)
var<storage, read_write> deposits: array<atomic<u32>>;

@group(0) @binding(3)
var<uniform> simulation: TrailSimulation;

fn readTrail(position: vec2i) -> vec4f {
    let maxPosition = vec2i(
        i32(simulation.width) - 1,
        i32(simulation.height) - 1
    );

    let p = clamp(
        position,
        vec2i(0),
        maxPosition
    );

    return textureLoad(
        previousTrail,
        p,
        0
    );
}

@compute
@workgroup_size(8, 8)
fn computeMain(
    @builtin(global_invocation_id) id: vec3u
) {
    if (
        id.x >= simulation.width ||
        id.y >= simulation.height
    ) {
        return;
    }

    let position = vec2i(
        i32(id.x),
        i32(id.y)
    );

    // Diffuse all four strain fields simultaneously.
    var sum = vec4f(0.0);

    for (var y = -1; y <= 1; y = y + 1) {
        for (var x = -1; x <= 1; x = x + 1) {
            sum += readTrail(
                position +
                vec2i(x, y)
            );
        }
    }

    let blurred = sum / 9.0;
    let current = readTrail(position);

    var trail = mix(
        current,
        blurred,
        simulation.diffusion
    );

    let pixelIndex =
        id.y *
        simulation.width +
        id.x;

    let depositBase =
        pixelIndex *
        4u;

    // Consume and clear each strain's atomic deposit counter.
    let deposit0 = atomicExchange(
        &deposits[depositBase],
        0u
    );

    let deposit1 = atomicExchange(
        &deposits[depositBase + 1u],
        0u
    );

    let deposit2 = atomicExchange(
        &deposits[depositBase + 2u],
        0u
    );

    let deposit3 = atomicExchange(
        &deposits[depositBase + 3u],
        0u
    );

    let depositVector = vec4f(
        f32(deposit0),
        f32(deposit1),
        f32(deposit2),
        f32(deposit3)
    );

    trail +=
        depositVector *
        simulation.depositStrength;

    trail *= simulation.decay;

    trail = clamp(
        trail,
        vec4f(0.0),
        vec4f(1.0)
    );

    // Clear channels for inactive strains.
    if (simulation.strainCount < 4u) {
        trail.a = 0.0;
    }

    if (simulation.strainCount < 3u) {
        trail.b = 0.0;
    }

    if (simulation.strainCount < 2u) {
        trail.g = 0.0;
    }

    textureStore(
        nextTrail,
        position,
        trail
    );
}
`;

export default trailShader;
